// src/components/FileUploader/index.tsx
import { FC, useRef } from 'react';
import { Upload } from 'antd';
import type { UploadProps } from 'antd';
import SvgIcon from '../SvgIcon';
import styles from './index.module.scss';
import { getExtensionsFromAccept, validateDocumentFile, type DocumentFileValidationResult } from '@/utils/validateDocumentFile';

interface FileUploaderProps {
  label: string;
  required?: boolean;
  optionalHint?: string; // 可选提示文本（如 "Optional but highly recommended"）
  hint: string;
  subHint: string;
  accept?: string;
  maxSize?: number;
  onChange?: UploadProps['onChange'];
  onFile?: (file: File) => void; // 新增：直接传递原生 File 对象
  onFileSelected?: (file: File, method: 'drag_drop' | 'click') => void;
  onInvalidFile?: (result: DocumentFileValidationResult) => void;
}

const { Dragger } = Upload;

const FileUploader: FC<FileUploaderProps> = ({ label, required = false, optionalHint, hint, subHint, accept = '.pdf,.docx,.txt', maxSize = 10, onChange, onFile, onFileSelected, onInvalidFile }) => {
  const lastMethodRef = useRef<'drag_drop' | 'click'>('click');
  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    accept,
    beforeUpload: (file) => {
      const allowedExtensions = getExtensionsFromAccept(accept);
      const validation = validateDocumentFile(file, { allowedExtensions, maxSizeMB: maxSize });
      if (!validation.isValid) {
        onInvalidFile?.(validation);
        return false;
      }
      onFile?.(file);
      onFileSelected?.(file, lastMethodRef.current);
      lastMethodRef.current = 'click';
      return false;
    },
    onChange,
    onDrop: () => {
      lastMethodRef.current = 'drag_drop';
    },
    showUploadList: false,
  };

  return (
    <div className={styles.fileUploader}>
      <div className={styles.label}>
        <div className={styles.labelLeft}>
          <span className={styles.labelText}>{label}</span>
          {required && <span className={styles.required}>*</span>}
        </div>
        {optionalHint && <span className={styles.optionalHint}>{optionalHint}</span>}
      </div>
      <Dragger {...uploadProps} className={styles.dragger}>
        <div className={styles.uploadContent}>
          <SvgIcon name="upload" size={44} className={styles.uploadIcon} />
          <p className={styles.hint}>{hint}</p>
          <p className={styles.subHint}>{subHint}</p>
        </div>
      </Dragger>
    </div>
  );
};

export default FileUploader;
